import { lazy, type LazyExoticComponent, type ComponentType, type ReactNode } from "react";
import { Routes, Route } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { LocaleProvider } from "@/lib/i18n/LocaleContext";
import { locales, localeUrlSlug, type Locale } from "@/lib/i18n/config";
import { ALL_KINDS, KIND_ROUTE_SLUG } from "@/lib/routing";
import { TRACKER_CATEGORY_SLUG } from "@/lib/tracker-routing";
import type { TrackerCategory } from "@/types/tracker";
import Home from "@/routes/Home";

// Every other route is lazy-loaded: the single eager bundle was ~2.9MB (751KB
// gzip), hurting LCP/TBT since a first-time visitor to any page downloaded
// every route's code. Layout (header/footer/nav chrome) stays eager since
// it's needed immediately; the Suspense boundary lives around <Outlet/> in
// Layout.tsx so header/footer never flash a loading state, only route
// content. Home is the exception and stays eager too: it's the most-landed
// route, and lazy-loading it meant the Suspense fallback (a bare min-h-[50vh]
// spinner) committed first, then got replaced by the full multi-section
// homepage once its chunk resolved — the footer's position jumped from just
// below the spinner to thousands of pixels down in that second commit,
// producing a measured mobile CLS of 0.867 (0.853 of it on the footer alone).
const ItemsPage = lazy(() => import("@/routes/ItemsPage"));
const ItemDetailPage = lazy(() => import("@/routes/ItemDetailPage"));
const CollectionPage = lazy(() => import("@/routes/CollectionPage"));
const Fish = lazy(() => import("@/routes/Fish"));
const Insects = lazy(() => import("@/routes/Insects"));
const Birds = lazy(() => import("@/routes/Birds"));
const Crops = lazy(() => import("@/routes/Crops"));
const Flowers = lazy(() => import("@/routes/Flowers"));
const Furniture = lazy(() => import("@/routes/Furniture"));
const Wardrobe = lazy(() => import("@/routes/Wardrobe"));
const Buildings = lazy(() => import("@/routes/Buildings"));
const Forageables = lazy(() => import("@/routes/Forageables"));
const FishDetailPage = lazy(() => import("@/features/fish/FishDetailPage"));
const InsectDetailPage = lazy(() => import("@/features/insects/InsectDetailPage"));
const BirdDetailPage = lazy(() => import("@/features/birds/BirdDetailPage"));
const CropDetailPage = lazy(() => import("@/features/crops/CropDetailPage"));
const FlowerDetailPage = lazy(() => import("@/features/flowers/FlowerDetailPage"));
const ForageableDetailPage = lazy(() => import("@/features/forageables/ForageableDetailPage"));
const AnimalsPageClient = lazy(() =>
  import("@/features/animals/AnimalsPageClient").then((m) => ({ default: m.AnimalsPageClient })),
);
const AnimalDetailPage = lazy(() => import("@/features/animals/AnimalDetailPage"));
const RecipesPageClient = lazy(() =>
  import("@/features/recipes/RecipesPageClient").then((m) => ({ default: m.RecipesPageClient })),
);
const RecipeDetailPage = lazy(() => import("@/features/recipes/RecipeDetailPage"));
const Locations = lazy(() => import("@/routes/Locations"));
const LocationDetail = lazy(() => import("@/routes/LocationDetail"));
const MapPage = lazy(() => import("@/routes/Map"));
const Npcs = lazy(() => import("@/routes/Npcs"));
const NpcDetailPage = lazy(() => import("@/features/npcs/NpcDetailPage"));
const Shops = lazy(() => import("@/routes/Shops"));
const Tracker = lazy(() => import("@/routes/Tracker"));
const GuidesPageClient = lazy(() =>
  import("@/features/guides/GuidesPageClient").then((m) => ({ default: m.GuidesPageClient })),
);
const GuideDetailPage = lazy(() => import("@/features/guides/GuideDetailPage"));
const AchievementsPageClient = lazy(() =>
  import("@/features/achievements/AchievementsPageClient").then((m) => ({ default: m.AchievementsPageClient })),
);
const AchievementDetailPage = lazy(() => import("@/features/achievements/AchievementDetailPage"));
const WeatherIndexPage = lazy(() =>
  import("@/features/weather/WeatherIndexPage").then((m) => ({ default: m.WeatherIndexPage })),
);
const WeatherDetailPage = lazy(() => import("@/features/weather/WeatherDetailPage"));
const EventsIndexPage = lazy(() =>
  import("@/features/events/EventsIndexPage").then((m) => ({ default: m.EventsIndexPage })),
);
const EventDetailPage = lazy(() => import("@/features/events/EventDetailPage"));
const PetsHubPage = lazy(() => import("@/features/pets/PetsHubPage").then((m) => ({ default: m.PetsHubPage })));
const PetsCatalogPage = lazy(() =>
  import("@/features/pets/PetsCatalogPage").then((m) => ({ default: m.PetsCatalogPage })),
);
const PetTraitsPage = lazy(() => import("@/features/pets/PetTraitsPage").then((m) => ({ default: m.PetTraitsPage })));
const PetsGuidePage = lazy(() => import("@/features/pets/PetsGuidePage").then((m) => ({ default: m.PetsGuidePage })));
const PetDetailPage = lazy(() => import("@/features/pets/PetDetailPage"));
const DailyChecklistPage = lazy(() =>
  import("@/features/daily-checklist/DailyChecklistPage").then((m) => ({ default: m.DailyChecklistPage })),
);
const CodesPage = lazy(() => import("@/features/codes/CodesPage").then((m) => ({ default: m.CodesPage })));
const WinterTrackerPage = lazy(() =>
  import("@/features/winter-tracker/WinterTrackerPage").then((m) => ({ default: m.WinterTrackerPage })),
);
const SupportPage = lazy(() => import("@/routes/SupportPage"));
const AboutPage = lazy(() => import("@/routes/AboutPage"));
const PrivacyPage = lazy(() => import("@/routes/PrivacyPage"));
const TermsPage = lazy(() => import("@/routes/TermsPage"));
const CookiesPage = lazy(() => import("@/routes/CookiesPage"));
const SitemapPage = lazy(() => import("@/routes/SitemapPage"));
const NewsPageClient = lazy(() =>
  import("@/features/news/NewsPageClient").then((m) => ({ default: m.NewsPageClient })),
);
const NewsDetailPage = lazy(() => import("@/features/news/NewsDetailPage"));
const NotFoundPage = lazy(() => import("@/routes/NotFoundPage"));
const AdminSuggestionsPage = lazy(() => import("@/features/admin/AdminSuggestionsPage"));

/** Kinds with a fully-ported page (real Next.js UI, not the generic CollectionPage fallback). */
const PORTED_COLLECTION_PAGES: Partial<Record<(typeof ALL_KINDS)[number], LazyExoticComponent<ComponentType>>> = {
  fish: Fish,
  insect: Insects,
  bird: Birds,
  crop: Crops,
  flower: Flowers,
  // `furniture` is also an ItemKind (10 curated records with real detail pages), so a separate
  // top-level /furniture route would tie with this kind's index route and lose. Hanging the
  // browse table here keeps one URL: the table at /furniture, the ten detail pages at
  // /furniture/:id, and nothing shadowed.
  furniture: Furniture,
  forageable: Forageables,
  animal: AnimalsPageClient,
  dish: RecipesPageClient,
};

/** Kinds with a fully-ported detail page (real EntityDetailLayout, not the generic ItemDetailPage). */
const PORTED_DETAIL_PAGES: Partial<Record<(typeof ALL_KINDS)[number], LazyExoticComponent<ComponentType>>> = {
  fish: FishDetailPage,
  insect: InsectDetailPage,
  bird: BirdDetailPage,
  crop: CropDetailPage,
  flower: FlowerDetailPage,
  forageable: ForageableDetailPage,
  animal: AnimalDetailPage,
  dish: RecipeDetailPage,
};

/**
 * Shared page routes, relative to whichever locale prefix mounts them (see
 * AppRouter below — this same tree is rendered once per locale).
 */
function commonRoutes(): ReactNode[] {
  return [
    <Route key="home" index element={<Home />} />,
    <Route key="items" path="items" element={<ItemsPage />} />,
    <Route key="items-detail" path="items/:id" element={<ItemDetailPage />} />,
    <Route key="locations" path="locations" element={<Locations />} />,
    <Route key="locations-map" path="locations/map" element={<MapPage />} />,
    <Route key="locations-detail" path="locations/:id" element={<LocationDetail />} />,
    <Route key="map" path="map" element={<MapPage />} />,
    <Route key="npcs" path="npcs" element={<Npcs />} />,
    <Route key="npcs-detail" path="npcs/:id" element={<NpcDetailPage />} />,
    <Route key="shops" path="shops" element={<Shops />} />,
    // Browse-table categories: hundreds of entries each, one page apiece, no per-item routes.
    // Their data is imported inside the lazily-loaded page module so it never reaches the
    // shared chunk (see features/furniture/data.ts for the measurement behind that).
    <Route key="wardrobe" path="wardrobe" element={<Wardrobe />} />,
    <Route key="buildings" path="buildings" element={<Buildings />} />,
    <Route key="tracker" path="tracker" element={<Tracker />} />,
    ...Object.entries(TRACKER_CATEGORY_SLUG).map(([category, slug]) => (
      <Route
        key={`tracker-${slug}`}
        path={`tracker/${slug}`}
        element={<Tracker initialCategory={category as TrackerCategory} />}
      />
    )),
    <Route key="guides" path="guides" element={<GuidesPageClient />} />,
    <Route key="guides-detail" path="guides/:slug" element={<GuideDetailPage />} />,
    <Route key="achievements" path="achievements" element={<AchievementsPageClient />} />,
    <Route key="achievements-detail" path="achievements/:slug" element={<AchievementDetailPage />} />,
    <Route key="weather" path="weather" element={<WeatherIndexPage />} />,
    <Route key="weather-detail" path="weather/:slug" element={<WeatherDetailPage />} />,
    <Route key="events" path="events" element={<EventsIndexPage />} />,
    <Route key="events-detail" path="events/:slug" element={<EventDetailPage />} />,
    <Route key="pets" path="pets" element={<PetsHubPage />} />,
    <Route key="pets-dogs" path="pets/dogs" element={<PetsCatalogPage species="dog" />} />,
    <Route key="pets-cats" path="pets/cats" element={<PetsCatalogPage species="cat" />} />,
    <Route key="pets-traits" path="pets/traits" element={<PetTraitsPage />} />,
    <Route key="pets-guide" path="pets/guide" element={<PetsGuidePage />} />,
    <Route key="pets-detail" path="pets/:id" element={<PetDetailPage />} />,
    <Route key="daily-checklist" path="daily-checklist" element={<DailyChecklistPage />} />,
    <Route key="codes" path="codes" element={<CodesPage />} />,
    <Route key="winter-tracker" path="tracking-winter-season" element={<WinterTrackerPage />} />,
    <Route key="support" path="support" element={<SupportPage />} />,
    <Route key="about" path="about" element={<AboutPage />} />,
    <Route key="privacy" path="privacy" element={<PrivacyPage />} />,
    <Route key="terms" path="terms" element={<TermsPage />} />,
    <Route key="cookies" path="cookies" element={<CookiesPage />} />,
    <Route key="sitemap" path="sitemap" element={<SitemapPage />} />,
    <Route key="news" path="news" element={<NewsPageClient />} />,
    <Route key="news-detail" path="news/:id" element={<NewsDetailPage />} />,
    <Route key="admin-suggestions" path="admin/suggestions" element={<AdminSuggestionsPage />} />,
    ...ALL_KINDS.map((kind) => {
      const slug = KIND_ROUTE_SLUG[kind];
      const PortedPage = PORTED_COLLECTION_PAGES[kind];
      const PortedDetailPage = PORTED_DETAIL_PAGES[kind];
      return (
        <Route key={kind} path={slug}>
          <Route index element={PortedPage ? <PortedPage /> : <CollectionPage kind={kind} />} />
          <Route path=":id" element={PortedDetailPage ? <PortedDetailPage /> : <ItemDetailPage />} />
        </Route>
      );
    }),
    <Route key="not-found" path="*" element={<NotFoundPage />} />,
  ];
}

export function AppRouter() {
  return (
    <Routes>
      {locales.map((locale: Locale) => {
        const slug = localeUrlSlug(locale);
        return (
          <Route
            key={locale}
            path={slug ? `/${slug}` : "/"}
            element={
              <LocaleProvider locale={locale}>
                <Layout />
              </LocaleProvider>
            }
          >
            {commonRoutes()}
          </Route>
        );
      })}
    </Routes>
  );
}
